import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import type { User, AuthResponse, LoginCredentials, RefreshTokenRequest, RefreshTokenResponse } from '@/types/auth';
import { CookieConst, webStorage, type AuthStorage } from './storage';
// import localizationService from '../services/localizationService';
import { MenuWorkflowMapper } from '@/lib/MenuWorkflowMapper'
import { ReadOnlyService } from '@yardi/yardi-react-framework'
import { environment as envConfig } from '@/config/environment';
import { TechUtils } from '@/feature/protected/RentalOptions/RentalOptionsUtils';
import { localStorageVariables } from '@/types/localStorageVariables';

export interface AuthConfig {
  apiBaseUrl: string;
  storage: AuthStorage;
}

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  handleAuthResponse: (response: AuthResponse) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function getIsReadOnlyByStatusNToken(): boolean {
  let isReadOnly = webStorage.getItem(CookieConst.RCReadOnlyUser) === "1";
  if (isReadOnly) {
    ReadOnlyService.setReadOnly(isReadOnly);
    return ReadOnlyService.isReadOnly();
  }
  else {
    const profilesData = JSON.parse(localStorage.getItem(localStorageVariables.Profiles) || '[]');
    const profiles = Array.isArray(profilesData) ? profilesData : [];
    const currentSitename = localStorage.getItem(localStorageVariables.sitename);
    const CurrentProspectProfile = profiles.find(profile => profile.SiteName === currentSitename);
    //if (CurrentProspectProfile) {
    //  const statusString = TechUtils.convertToString(CurrentProspectProfile.PrimaryProspectStatus).toLowerCase();

    //  if (TechUtils.convertToBoolean(CurrentProspectProfile.IsRoommateApp)) {
    //    if (statusString == "denied" || statusString == "canceled") {
    //      ReadOnlyService.setReadOnly(true);
    //    }
    //    else {
    //      ReadOnlyService.setReadOnly(false);
    //    }
    //  }
    //  else {
    //    if (statusString == "applied" || statusString == "approved" || statusString == "denied" || statusString == "canceled") {
    //      ReadOnlyService.setReadOnly(true);
    //    }
    //    else {
    //      ReadOnlyService.setReadOnly(false);
    //    }
    //  }
    //}
    //else {
    //  ReadOnlyService.setReadOnly(false);
      //}
      if (CurrentProspectProfile) {
          ReadOnlyService.setReadOnly(CurrentProspectProfile.IsReadOnlyUser);
      }
    return ReadOnlyService.isReadOnly();
  }
}

export function createAuthProvider(config: AuthConfig) {
  function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const isRefreshing = useRef(false);

    // Function to clear auth state
    const clearAuthState = useCallback(async () => {
      try {
        await Promise.all([
          config.storage.removeUser(),
          // by removing on logout, we ensure that server issues a new session id on next request
          // then redis fetching that uses keys like '{sessionid}{someString}' will not be getting data
          // from previous session, this is particularly problematic with the prospect profile
          config.storage.removeYsiSessionID(),
        ]);
        setUser(null);
        // Reset read-only on logout/clear
        ReadOnlyService.setReadOnly(false);

        // Also clean up any old Angular storage that might still exist
        localStorage.removeItem('ls.authorizationData');
        localStorage.removeItem('ls.LoginGUID');
      } catch (error) {
        console.error('Failed to clear auth state:', error);
      }
    }, []);

    // Initialize auth state from storage
    useEffect(() => {
      async function initAuth() {
        try {
          console.log('Initializing auth state from storage...');
          const [storedUser] = await Promise.all([
            config.storage.getUser(),
          ]);

          console.log('Retrieved from storage:', {
            storedUser: !!storedUser
          });

          if (storedUser) {
            // Sync read-only from token on init
            ReadOnlyService.setReadOnly(getIsReadOnlyByStatusNToken());
            setUser(storedUser);

            // Initialize MenuWorkflowStep from localStorage after authentication restore
            // This ensures proper menu milestone display on page refresh
              setTimeout(() => {
                MenuWorkflowMapper.initializeAfterLogin();
              }, 100); // Small delay to ensure localStorage is ready
            
          } else {
            console.log('No stored auth state found');
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          await clearAuthState();
        } finally {
          setIsLoading(false);
        }
      }

      initAuth();
    }, [clearAuthState]);

    const handleAuthResponse = async (response: AuthResponse) => {
      try {
        console.log('Handling auth response:', {
          hasToken: !!response.token,
          hasRefreshToken: !!response.refreshToken,
          hasUser: !!response.user,
          userEmail: response.user?.email,
          tokenPreview: response.token ? response.token.substring(0, 20) + '...' : 'none'
        });

        if (!response.token || !response.user) {
          console.error('Invalid auth response:', response);
          throw new Error('Invalid auth response: missing token or user');
        }

        // Validate the new token before storing
        const tempToken = response.token;
        console.log('Validating JWT token structure...');

        const isValid = (() => {
          try {
            const parts = tempToken.split('.');
            if (parts.length !== 3) {
              console.error('JWT validation failed: Invalid format, expected 3 parts, got', parts.length);
              return false;
            }

            const payload = JSON.parse(atob(parts[1]));
            console.log('JWT payload:', {
              iss: payload.iss,
              aud: payload.aud,
              exp: payload.exp,
              iat: payload.iat,
              hasExpClaim: !!payload.exp
            });

            const currentTime = Math.floor(Date.now() / 1000);

            // Check expiration
            if (payload.exp && payload.exp <= currentTime) {
              console.error('JWT validation failed: Token expired', {
                exp: payload.exp,
                currentTime,
                expiredAgo: currentTime - payload.exp
              });
              return false;
            }

            // Note: Angular doesn't validate issuer/audience, so we'll skip those checks
            // to match Angular's behavior and allow OAuth2 tokens with any iss/aud values

            console.log('JWT validation passed successfully');
            return true;
          } catch (parseError) {
            console.error('JWT validation failed: Parse error', parseError);
            return false;
          }
        })();

        if (!isValid) {
          console.error('JWT token validation failed, rejecting auth response');
          throw new Error('Received invalid JWT token from server');
        }

        console.log('JWT token validated successfully, storing auth state...');
        await Promise.all([
          config.storage.setUser(response.user),
        ]);

        console.log('Auth state stored, updating context state...');
        setUser(response.user);
        // localizationService.setLanguage(response.user.culture || 'en');

        // Sync read-only from new token
        ReadOnlyService.setReadOnly(getIsReadOnlyByStatusNToken());

        console.log('Auth state updated successfully - user should now be logged in');
      } catch (error) {
        console.error('Failed to handle auth response:', error);
        console.log('Clearing auth state due to error');
        // Clear any partial state
        await clearAuthState();
        throw error;
      }
    };

    const login = async (credentials: LoginCredentials) => {
      try {
        const response = await fetch(envConfig.apiPrefix.rootPath + `/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(credentials),
        });

        if (!response.ok) {
          throw new Error('Login failed');
        }

        const authResponse: AuthResponse = await response.json();
        await handleAuthResponse(authResponse);
      } catch (error) {
        console.error('Login failed:', error);
        throw error;
      }
    };

    const logout = async () => {
      try {
        console.log('Logging out user');

        try {
          await fetch(envConfig.apiPrefix.rootPath + `logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({} as RefreshTokenRequest),
          });
        } catch (error) {
          console.warn('Failed to revoke refresh token on server:', error);
        }

        await clearAuthState();
      } catch (error) {
        console.error('Logout failed:', error);
        throw error;
      }
    };

    return (
      <AuthContext.Provider
        value={{
          user,
          isLoading,
          login,
          logout,
          handleAuthResponse
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return AuthProvider;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 